import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Video, Film, Clock, TrendingUp, Zap, Target, 
  MessageSquare, BarChart3, PlayCircle, Star,
  Sparkles, ChevronDown, ChevronUp, Mic, Youtube
} from 'lucide-react';
import { formatTime, formatTimeRange } from '@/utils/timeFormat';

const VideoAnalysisResults = ({ result, darkMode }) => {
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [combinedInsightsExpanded, setCombinedInsightsExpanded] = useState(true);
  
  console.log('🎥 VideoAnalysisResults rendering!');
  console.log('🎥 result:', result);
  console.log('🎥 video_insights:', result.video_insights);
  
  const videoInsights = result.video_insights || {};
  const duration = videoInsights.duration || 0;
  const hasAudio = videoInsights.has_audio || false;
  const transcript = videoInsights.transcript || '';
  const transcriptSegments = videoInsights.transcript_segments || [];
  const speakingPace = videoInsights.speaking_pace || 'moderate';
  const audioSentiment = videoInsights.audio_sentiment || 'neutral';
  const hookAnalysis = videoInsights.hook_analysis || {};
  const keyMoments = videoInsights.key_moments || [];
  const sceneBreakdown = videoInsights.scene_breakdown || [];
  const pacingAnalysis = videoInsights.pacing_analysis || {};
  const ctaTiming = videoInsights.cta_timing || {};
  const audioVisualAlignment = videoInsights.audio_visual_alignment || 0;
  const combinedInsights = videoInsights.combined_insights || {};

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Video Overview */}
      <Card className={darkMode ? 'bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border-2 border-blue-500 shadow-xl' : 'bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg'}>
        <CardHeader className="pb-3">
          <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Video className="w-5 h-5 text-blue-500" />
            Video Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Video Thumbnail */}
          {result.image_data && (
            <div className="mb-4">
              <img 
                src={result.image_data.startsWith('data:') ? result.image_data : `data:image/jpeg;base64,${result.image_data}`}
                alt="Video frame"
                className="w-full max-h-48 object-contain rounded-lg"
              />
            </div>
          )}
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-blue-800/40' : 'bg-white'}`}>
              <Clock className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Duration</div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatTime(duration)}</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-blue-800/40' : 'bg-white'}`}>
              <Film className="w-5 h-5 mx-auto mb-1 text-purple-500" />
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Frames</div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{videoInsights.frames_analyzed || 0}</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-blue-800/40' : 'bg-white'}`}>
              <MessageSquare className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Audio</div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{hasAudio ? 'Yes' : 'No'}</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-blue-800/40' : 'bg-white'}`}>
              <BarChart3 className="w-5 h-5 mx-auto mb-1 text-orange-500" />
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Alignment</div>
              <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{audioVisualAlignment}%</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Frame-by-Frame Analysis */}
      {videoInsights.emotion_timeline && videoInsights.emotion_timeline.length > 0 && (
        <Card className={darkMode ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-2 border-purple-500 shadow-xl' : 'bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg'}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <PlayCircle className="w-5 h-5 text-purple-500" />
              Frame-by-Frame Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {videoInsights.emotion_timeline.map((frame, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-purple-800/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-purple-500">Frame at {formatTime(frame.timestamp)}</span>
                  <Badge variant="outline">Timestamp</Badge>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{frame.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Hook Analysis */}
      <Card className={darkMode ? 'bg-gradient-to-br from-red-900/40 to-orange-900/40 border-2 border-red-500 shadow-xl' : 'bg-gradient-to-br from-red-50 to-orange-50 shadow-lg'}>
        <CardHeader className="pb-3">
          <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Zap className="w-5 h-5 text-red-500" />
            Hook Analysis (First 3 Seconds)
            <Badge className={`ml-2 ${hookAnalysis.score >= 75 ? 'bg-green-600' : hookAnalysis.score >= 50 ? 'bg-yellow-600' : 'bg-red-600'}`}>
              {hookAnalysis.score || 0}% Effective
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="w-full bg-gray-700 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all ${hookAnalysis.score >= 75 ? 'bg-green-500' : hookAnalysis.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${hookAnalysis.score || 0}%` }}
            />
          </div>
          <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <p className="font-semibold mb-2">Recommendations:</p>
            <ul className="space-y-1 ml-4">
              {(hookAnalysis.recommendations || []).map((rec, idx) => (
                <li key={idx} className="list-disc">{rec}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Key Moments */}
      {keyMoments.length > 0 && (
        <Card className={darkMode ? 'bg-gradient-to-br from-yellow-900/40 to-amber-900/40 border-2 border-yellow-500 shadow-xl' : 'bg-gradient-to-br from-yellow-50 to-amber-50 shadow-lg'}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Star className="w-5 h-5 text-yellow-500" />
              Key Moments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {keyMoments.map((moment, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-yellow-800/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-yellow-500">{moment.label}</span>
                  <Badge variant="outline">{formatTime(moment.timestamp)}</Badge>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{moment.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Scene Breakdown */}
      {sceneBreakdown.length > 0 && (
        <Card className={darkMode ? 'bg-gradient-to-br from-teal-900/40 to-cyan-900/40 border-2 border-teal-500 shadow-xl' : 'bg-gradient-to-br from-teal-50 to-cyan-50 shadow-lg'}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Film className="w-5 h-5 text-teal-500" />
              Scene Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sceneBreakdown.map((scene, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-teal-800/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-teal-500">{scene.scene}</span>
                  <Badge variant="outline">{scene.time_range}</Badge>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{scene.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Audio Transcript */}
      {hasAudio && transcript && transcript.length > 20 && !transcript.includes('[No audio') && (
        <Card className={darkMode ? 'bg-gradient-to-br from-orange-900/40 to-red-900/40 border-2 border-orange-500 shadow-xl' : 'bg-gradient-to-br from-orange-50 to-red-50 shadow-lg'}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Mic className="w-5 h-5 text-orange-500" />
                Audio Transcript
                <Badge className="ml-2 bg-orange-600 capitalize">{speakingPace} Pace</Badge>
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                className={darkMode ? 'text-white hover:bg-orange-800/30' : 'text-gray-700 hover:bg-orange-100'}
              >
                {transcriptExpanded ? (
                  <>Collapse <ChevronUp className="ml-1 w-4 h-4" /></>
                ) : (
                  <>Expand <ChevronDown className="ml-1 w-4 h-4" /></>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-orange-800/30' : 'bg-white'}`}>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${transcriptExpanded ? '' : 'line-clamp-4'}`}>
                "{transcript}"
              </p>
              {transcript.length > 200 && !transcriptExpanded && (
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {transcript.length} characters • Click "Expand" to see full transcript
                </p>
              )}
            </div>
            
            {transcriptSegments && transcriptSegments.length > 0 && transcriptExpanded && (
              <div className="mt-4">
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Timestamped Segments:
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {transcriptSegments.map((segment, idx) => (
                    <div key={idx} className={`p-2 rounded ${darkMode ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                      <span className={`text-xs font-mono ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                        [{formatTime(segment.start)} - {formatTime(segment.end)}]
                      </span>
                      <span className={`text-sm ml-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {segment.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Combined Video + Audio Insights (for Creators) */}
      {combinedInsights && Object.keys(combinedInsights).length > 0 && !combinedInsights.error && (
        <Card className={darkMode ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-2 border-purple-500 shadow-xl' : 'bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg'}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Sparkles className="w-5 h-5 text-purple-500" />
                Creator Insights
                <Badge className="ml-2 bg-gradient-to-r from-purple-600 to-pink-600">AI Enhanced</Badge>
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setCombinedInsightsExpanded(!combinedInsightsExpanded)}
                className={darkMode ? 'text-white hover:bg-purple-800/30' : 'text-gray-700 hover:bg-purple-100'}
              >
                {combinedInsightsExpanded ? (
                  <>Collapse <ChevronUp className="ml-1 w-4 h-4" /></>
                ) : (
                  <>Expand <ChevronDown className="ml-1 w-4 h-4" /></>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className={`space-y-4 ${combinedInsightsExpanded ? '' : 'hidden'}`}>
            {/* Hook Quality */}
            {combinedInsights.hook_quality && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-800/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Hook Quality (First 3-5s)
                  </p>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="border-purple-500">
                      Visual: {combinedInsights.hook_quality.visual_score}/100
                    </Badge>
                    <Badge variant="outline" className="border-pink-500">
                      Audio: {combinedInsights.hook_quality.audio_score}/100
                    </Badge>
                  </div>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {combinedInsights.hook_quality.analysis}
                </p>
              </div>
            )}

            {/* Audio-Visual Sync */}
            {combinedInsights.audio_visual_sync && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-800/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Audio-Visual Synchronization
                  </p>
                  <Badge className="bg-purple-600">
                    {combinedInsights.audio_visual_sync.score}% Aligned
                  </Badge>
                </div>
                <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {combinedInsights.audio_visual_sync.analysis}
                </p>
                {combinedInsights.audio_visual_sync.sync_moments && combinedInsights.audio_visual_sync.sync_moments.length > 0 && (
                  <div className="space-y-1">
                    {combinedInsights.audio_visual_sync.sync_moments.slice(0, 3).map((moment, idx) => (
                      <div key={idx} className={`text-xs p-2 rounded ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                        <span className="font-mono text-purple-500">[{formatTime(moment.timestamp)}]</span>
                        <span className={`ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {moment.type}: {moment.note}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Talking Points */}
            {combinedInsights.talking_points && combinedInsights.talking_points.length > 0 && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-800/30' : 'bg-white'}`}>
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Key Talking Points
                </p>
                <div className="space-y-2">
                  {combinedInsights.talking_points.map((point, idx) => (
                    <div key={idx} className={`p-2 rounded ${darkMode ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-mono ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          [{formatTime(point.timestamp)}]
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {point.importance}
                        </Badge>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {point.topic}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Engagement Moments */}
            {combinedInsights.engagement_moments && combinedInsights.engagement_moments.length > 0 && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-800/30' : 'bg-white'}`}>
                <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  High Engagement Moments
                </p>
                <div className="space-y-2">
                  {combinedInsights.engagement_moments.map((moment, idx) => (
                    <div key={idx} className={`p-2 rounded ${darkMode ? 'bg-pink-900/20' : 'bg-pink-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-mono ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                          [{formatTime(moment.timestamp)}]
                        </span>
                        <Badge className="bg-pink-600 text-xs">
                          {moment.retention_score}% Retention
                        </Badge>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {moment.reason}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Creator Recommendations */}
            {combinedInsights.creator_recommendations && (
              <div className={`p-4 rounded-lg border-2 ${darkMode ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-purple-500' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'}`}>
                <p className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Youtube className="w-4 h-4 text-red-500" />
                  Actionable Recommendations
                </p>
                
                {/* YouTube Recommendations */}
                {combinedInsights.creator_recommendations.youtube && (
                  <div className="mb-3">
                    <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      For YouTube:
                    </p>
                    <ul className={`text-sm space-y-1 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {combinedInsights.creator_recommendations.youtube.map((rec, idx) => (
                        <li key={idx} className="list-disc">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Podcast Recommendations */}
                {combinedInsights.creator_recommendations.podcast && (
                  <div className="mb-3">
                    <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      For Podcasts:
                    </p>
                    <ul className={`text-sm space-y-1 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {combinedInsights.creator_recommendations.podcast.map((rec, idx) => (
                        <li key={idx} className="list-disc">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* General Recommendations */}
                {combinedInsights.creator_recommendations.general && (
                  <div>
                    <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      General Tips:
                    </p>
                    <ul className={`text-sm space-y-1 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {combinedInsights.creator_recommendations.general.map((rec, idx) => (
                        <li key={idx} className="list-disc">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pacing & CTA Recommendations */}
      <Card className={darkMode ? 'bg-gradient-to-br from-green-900/40 to-emerald-900/40 border-2 border-green-500 shadow-xl' : 'bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'}>
        <CardHeader className="pb-3">
          <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <Target className="w-5 h-5 text-green-500" />
            Pacing & Call-to-Action
            <Badge className="ml-2 bg-green-600">{pacingAnalysis.score || 0}%</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pacing Recommendations:</p>
            <ul className={`text-sm space-y-1 ml-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {(pacingAnalysis.recommendations || []).map((rec, idx) => (
                <li key={idx} className="list-disc">{rec}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>CTA Placement:</p>
            <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-800/30' : 'bg-white'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Optimal CTA Time: <span className="font-bold text-green-500">{formatTime(ctaTiming.optimal_time || 0)}</span>
              </p>
              <ul className={`text-xs space-y-1 ml-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {(ctaTiming.recommendations || []).map((rec, idx) => (
                  <li key={idx} className="list-disc">{rec}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoAnalysisResults;
